/* @img-migrated */
import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Upload,
  X,
  Loader2,
  File as FileIcon,
  FileText,
  Image as ImageIcon,
  Film,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';

import Img from './Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resolveUrl = (url) =>
  resolveImg(url);

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const iconForType = (ct = '') => {
  if (ct.startsWith('image/')) return <ImageIcon className="w-4 h-4" />;
  if (ct.startsWith('video/')) return <Film className="w-4 h-4" />;
  if (ct.includes('pdf') || ct.includes('word') || ct.includes('text')) return <FileText className="w-4 h-4" />;
  return <FileIcon className="w-4 h-4" />;
};

/**
 * MultiFileUploader — reusable multi-file picker for images + videos + documents.
 *
 * Props:
 *   value       - Array<{url, filename, content_type, size}> (controlled)
 *   onChange    - (files: Array) => void
 *   max         - maximum files (default 10, hard cap 10)
 *   maxMB       - per-file size cap in MB (default 10)
 *   endpoint    - absolute API endpoint (default marketing-requests/upload)
 *   accept      - input[type=file] accept attribute
 *   label       - primary CTA label (default "Upload Files")
 *   helper      - helper copy under the CTA
 *   dataTestId  - test id prefix
 */
export const MultiFileUploader = ({
  value = [],
  onChange,
  max = 10,
  maxMB = 10,
  endpoint = `${API}/marketing-requests/upload`,
  accept = 'image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip',
  label = 'Upload Files',
  helper,
  dataTestId = 'multi-file-uploader',
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const MAX_BYTES = maxMB * 1024 * 1024;

  const handleFiles = useCallback(
    async (fileList) => {
      const all = Array.from(fileList || []);
      if (all.length === 0) return;

      const remaining = max - value.length;
      if (remaining <= 0) {
        toast.error(`Maximum ${max} files reached`);
        return;
      }

      const batch = all.slice(0, remaining);
      if (all.length > remaining) {
        toast.info(`Only adding ${remaining} of ${all.length} files (max ${max})`);
      }

      // Client-side size check (real validation still happens on server)
      for (const f of batch) {
        if (f.size > MAX_BYTES) {
          toast.error(`"${f.name}" exceeds ${maxMB} MB limit`);
          return;
        }
      }

      setUploading(true);
      setProgress(0);
      try {
        const fd = new FormData();
        batch.forEach((f) => fd.append('files', f));

        const res = await axios.post(endpoint, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
          },
        });

        const newFiles = res.data?.files || [];
        onChange([...value, ...newFiles]);
        if (newFiles.length > 0) {
          toast.success(`${newFiles.length} file${newFiles.length !== 1 ? 's' : ''} uploaded`);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to upload files');
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [value, max, maxMB, MAX_BYTES, endpoint, onChange]
  );

  const handleInputChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));

  const atMax = value.length >= max;

  return (
    <div data-testid={dataTestId}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        onChange={handleInputChange}
        className="hidden"
        data-testid={`${dataTestId}-input`}
      />

      {!atMax && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`rounded-xl border-2 border-dashed transition-colors ${
            dragOver ? 'border-[#002249] bg-[#002249]/5' : 'border-slate-200 hover:border-[#002249]/40'
          }`}
        >
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-full flex flex-col items-center justify-center py-6 sm:py-8 px-4 text-center focus:outline-none"
            data-testid={`${dataTestId}-btn`}
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 sm:w-7 sm:h-7 text-[#002249] mb-2 animate-spin" />
            ) : (
              <Upload className="w-6 h-6 sm:w-7 sm:h-7 text-[#65676B] mb-2" />
            )}
            <span className="text-[14px] font-semibold text-[#050505]">
              {uploading ? `Uploading ${progress}%` : label}
            </span>
            <span className="text-[12px] text-[#65676B] mt-0.5">
              {helper || `Drag & drop or click · up to ${max} files · ${maxMB} MB each`}
            </span>
          </button>
        </div>
      )}

      {value.length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`${dataTestId}-list`}>
          <div className="flex items-center justify-between px-0.5">
            <span className="text-[12px] font-medium text-[#65676B]" data-testid={`${dataTestId}-count`}>
              {value.length} / {max} file{value.length !== 1 ? 's' : ''}
            </span>
            {atMax && <span className="text-[11px] font-semibold text-amber-600">Max reached</span>}
          </div>
          {value.map((f, i) => {
            const isImage = (f.content_type || '').startsWith('image/');
            return (
              <div
                key={`${f.url}-${i}`}
                className="flex items-center gap-3 px-3 py-2 rounded-lg border border-slate-200 bg-white"
                data-testid={`${dataTestId}-item-${i}`}
              >
                <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0 text-slate-500">
                  {isImage ? (
                    <Img variant="generic"
                      src={resolveUrl(f.url)}
                      alt={f.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    iconForType(f.content_type)
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-slate-800 truncate" title={f.filename}>
                    {f.filename}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {formatBytes(f.size)}
                    {f.content_type ? ` · ${f.content_type}` : ''}
                  </p>
                </div>
                <a
                  href={resolveUrl(f.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-md text-slate-400 hover:text-[#002249] hover:bg-slate-100 flex items-center justify-center"
                  title="Open / download"
                  data-testid={`${dataTestId}-open-${i}`}
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  className="w-8 h-8 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center"
                  title="Remove"
                  data-testid={`${dataTestId}-remove-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
          {uploading && (
            <div className="h-1 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-[#002249] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiFileUploader;
